<template>
  <div class="opportunities__table">
    <LpTable
      :columns="tableColumns"
      :data="tableData"
      :loading="!tableRows.length"
      :pagination="{ pageSize: 10 }"
      :row-key="(row) => row.opportunityId"
      :row-props="rowProps"
      :ui="{
        row: '.row',
      }"
      class="table"
    >
      <template #impactLabel="{ data }">
        <LpTag
          :class="[
            'inline-flex mr-4 whitespace-nowrap',
            data.impact + '-level'
          ]"
          size="medium"
        >
          <span class="text-sm text-base-900">
            {{ data.impactLabel }}
          </span>
        </LpTag>
      </template>
      <template #uplift="{ data }">
        <span class="capitalize">
          <span
            v-if="isDisparities"
            class="capitalize"
          >
            {{ data.impact === 'max' ? $t('Over 100%') : `-${formatValue(data.uplift, percentFormat)}` }}
          </span>
          <span
            v-else
            class="capitalize"
          >
            {{ data.impact === 'max' ? $t('Over 100%') : `+${formatValue(data.uplift, percentFormat)}` }}
          </span>
        </span>
      </template>
      <template #lostRevenue="{ data }">
        <span class="capitalize">
          <span v-if="isDisparities">
            {{ data.lostRevenue }}
          </span>
          <span v-else>
            {{ data.impact === 'max' ? $t('Over') + ' ' + data.lostRevenue : data.lostRevenue }}
          </span>
        </span>
      </template>
      <template #navLink>
        <LpBasicLink
          class="mt-1"
          color="gray"
        >
          <ArrowRightIcon class="w-5 h-5 arrow-icon" />
        </LpBasicLink>
      </template>
    </LpTable>
  </div>
</template>

<script>
import { computed } from 'vue';

import { orderBy } from 'lodash-es';

import { LpTable } from '@thehotelsnetwork/lampo';
import { ArrowRightIcon } from '@thehotelsnetwork/icons/vue/linear';

import { percentFormat } from '@/config/valueFormatConfig';
import i18n from '@/config/i18n';

import useMainState from '@/state/useMainState';

import {
  track,
} from '@/utils/analytics';
import formatValue from '@/utils/formatValue';

export default {
  components: {
    ArrowRightIcon,
    LpTable,
  },
  props: {
    tableRows: {
      type: Array,
      default: () => [],
    },
    isDisparities: {
      type: Boolean,
    },
  },
  setup(props) {
    const { t } = i18n.global;

    const {
      setProperty,
    } = useMainState();

    const tableColumns = [
      {
        key: 'name',
        label: t('Property Name'),
        width: '30%',
      },
      {
        key: 'impactLabel',
        label: t('Impact'),
        width: '15%',
      },
      {
        key: 'uplift',
        label: props.isDisparities ? t('Estimated drop') : t('Estimated Uplift'),
        width: '20%',
      },
      {
        key: 'lostRevenue',
        label: props.isDisparities ? t('Est. Dropped Disparities') : t('Estimated Lost Revenue'),
        width: '25%',
      },
      {
        key: 'navLink',
        label: '',
        width: '10%',
      },
    ];

    const tableData = computed(() => orderBy(props.tableRows, ['uplift'], ['desc']));

    function handleOpportunityClick(row) {
      track('click - overview opportunity');
      setProperty(row.propertyId);
    }

    const rowProps = (row) => ({
      style: { cursor: 'pointer' },
      onClick: () => handleOpportunityClick(row),
    });

    return {
      handleOpportunityClick,
      tableColumns,
      tableData,
      rowProps,
      formatValue,
      percentFormat,
    };
  },
};

</script>

<style scoped>
  .lp-tag {
    @apply mb-3 lg:mb-0;
    &.high-level,
    &.max-level {
      @apply bg-danger-300;
    }
    &.medium-level {
      @apply bg-warning-300;
    }
    &.low-level {
      @apply bg-success-300;
    }
  }
  .table :deep(.row) {
    @apply cursor-pointer;
    &:hover {
      @apply bg-base-100;
    }
  }
</style>
